import { http } from './index'

export interface ShiftCopyRequest {
    department_id: string
    employment_ids: string[]
    source_dates: string[]
    target_dates: string[]
}

export interface CopyError {
    code: string
    message: string
    details?: Record<string, unknown>
}

export interface ShiftCopyPreviewItem {
    employment_id: string
    employee_name: string
    can_copy: boolean
    errors: CopyError[]
    source_shifts_count: number
    shifts_to_delete: Array<{
        id: string
        date: string
        plan_work_start_at: string | null
        plan_work_end_at: string | null
        day_type: string | null
    }>
    shifts_to_create_count: number
}

export interface ShiftCopyPreviewResponse {
    can_copy: boolean
    errors: CopyError[]
    preview: {
        source_days_count: number
        target_days_count: number
        employments: ShiftCopyPreviewItem[]
        total_shifts_to_delete: number
        total_shifts_to_create: number
    } | null
}

export interface ShiftCopyExecuteResponse {
    success: boolean
    result?: {
        shifts_deleted: number
        shifts_created: number
        employments_succeeded: number
        employments_failed: number
        employments: Array<{
            employment_id: string
            success: boolean
            shifts_deleted?: number
            shifts_created?: number
            errors?: CopyError[]
        }>
    }
    errors?: CopyError[]
}

export interface BulkDeleteShiftsRequest {
    shift_ids: string[]
}

export interface BulkDeleteShiftsResponse {
    deleted_count: number
}

export async function getSchedule(params: any) {
    return await http.get<any>(`/api/shifts/schedule/`, {params})
}

export async function getStats(params: any) {
    return await http.get<any>(`/api/shifts/employee-statistics/`, { params })
}

export async function getProductionCalendar(department: string, params: any) {
    return await http.get<any>(`/api/departments/${department}/production-calendar/`, {params})
}

export async function getTimeSheet(data:any) {
    return await http.post<any>(`/api/timesheets/`, data)
}
  
export async function getPositions() {
    return await http.get<any>('/api/positions/')
}

export async function editPosition(id: string, data: { short_name?: string | null, shift_schedule?: string | null }) {
    return await http.patch<any>(`/api/positions/${id}/`, data)
}

export async function getDayTypes() {
    return await http.get<any>('/api/day-types/')
}

export async function createShift(data:any) {
    return await http.post<any>(`/api/shifts/`, data)
}

export async function validateShift(data:any) {
    return await http.post<any>(`/api/shifts/validate/`, data)
}

export async function editShift(data:any) {
    return await http.patch<any>(`/api/shifts/${data.id}/`, data)
}

export async function removeShift(id:string | number) {
    return await http.delete<any>(`/api/shifts/${id}/`)
}

export async function createUpdateShiftPack(data:any) {
    return await http.post<any>('/api/shifts/bulk-update-or-create/', data)
}

export async function getShiftSchedule() {
    return await http.get<any>(`/api/shift-schedule/`)
}

export async function getTimesheetReport(data:any) {
    return await http.post<any>(`/api/report/timesheet-report/`, data, {
        responseType: 'blob'
    })
}

export async function getScheduleReport(data:any) {
    return await http.post<any>(`/api/report/schedule-signature/`, data, {
        responseType: 'blob'
    })
}

export async function getConsolidatedReport(data:any) {
    return await http.post<any>(`/api/report/consolidated-report/`, data, {
        responseType: 'blob'
    })
}

export async function getDisciplineReport(data:any) {
    return await http.post<any>(`/api/report/discipline/`, data, {
        responseType: 'blob'
    })
}

export async function getFraudReport(data:any) {
    return await http.post<any>(`/api/report/fraud/`, data, {
        responseType: 'blob'
    })
}

export async function getManualMarksReport(data:any) {
    return await http.post<any>(`/api/report/manual-marks/`, data, {
        responseType: 'blob'
    })
}

export async function getShiftHistory(id:string) {
    return await http.get<any>(`/api/shifts/${id}/history/`)
}

export async function cleanSchedule(data:any) {
    return await http.post<any>(`/api/shifts/cleanup-schedule/`, data)
}

export async function generateSchedule(data:any) {
    return await http.post<any>(`/api/shifts/auto-generate/`, data)
}

export async function previewShiftCopy(data: ShiftCopyRequest) {
    return await http.post<{ body: ShiftCopyPreviewResponse }>('/api/shifts/copy/preview/', data, {
        suppressGlobalErrorToast: true,
    })
}

export async function executeShiftCopy(data: ShiftCopyRequest) {
    return await http.post<{ body: ShiftCopyExecuteResponse }>('/api/shifts/copy/', data, {
        suppressGlobalErrorToast: true,
    })
}

export async function bulkDeleteShifts(data: BulkDeleteShiftsRequest) {
    return await http.post<{ body: BulkDeleteShiftsResponse, msg: string, status: 'ok' }>('/api/shifts/bulk-delete/', data, {
        suppressGlobalErrorToast: true,
    })
}
